import DOMPurify from "dompurify";

/**
 * The concierge guide is written by an admin in the DMS as a single rich-text
 * document per city, per language (Properties → Destination). The design shows
 * it as a stack of numbered cards, so the document is cut back into cards here.
 *
 * The contract with the admin is the numbering: a heading that begins with
 * "1.", "2." … opens a new card. That is already how both city guides are
 * written, and it is the one rule the DMS editor asks authors to keep.
 */

export interface GuideCard {
  /** Two-digit label for the card's gold eyebrow — "01", "02" … */
  num: string;
  /** Heading text with the leading number and any emoji removed. */
  title: string;
  /** The card's body, already sanitised. */
  html: string;
}

export interface DestinationGuide {
  /** Opening heading of the document, if it has one. */
  title: string | null;
  /**
   * First paragraph of the document, used as the panel's standfirst. Sanitised
   * inline HTML rather than plain text, so the bold the admin applied in the
   * editor survives onto the page.
   */
  description: string | null;
  cards: GuideCard[];
  /** Closing sign-off ("Bon voyage …"), shown beside the plane. */
  footer: string | null;
}

/**
 * Quill's own output plus what an author is likely to paste in from a doc.
 * `style` is deliberately absent: pasted copy arrives carrying its source's
 * colours and font sizes, which would fight the card design.
 */
const SANITIZE_CFG = {
  ALLOWED_TAGS: [
    "h1", "h2", "h3", "h4", "h5", "h6",
    "p", "br", "div", "span",
    "strong", "b", "em", "i", "u", "s", "sub", "sup",
    "ul", "ol", "li",
    "blockquote", "pre", "code",
    "a", "img",
  ],
  ALLOWED_ATTR: ["href", "target", "rel", "src", "alt"],
};

/** A heading that opens a card: "1. Arrival", "2) Money" … */
const NUMBERED_RE = /^\s*\d+\s*[.)]/;

const isHeading = (el: Element) => /^H[1-6]$/.test(el.tagName);

/** `textContent` leaves &nbsp; as U+00A0, which trims and matches badly. */
const textOf = (el: Element) =>
  (el.textContent || "").replace(/ /g, " ").replace(/\s+/g, " ").trim();

/**
 * Only the leading "1." goes — the card's gold label already carries the
 * number. Emoji stay: the admin puts one on every heading in the DMS on
 * purpose, and the guide should read on the site as it does in the editor.
 */
const cleanTitle = (raw: string) =>
  raw.replace(NUMBERED_RE, "").replace(/\s+/g, " ").trim();

/**
 * Quill writes `<p><br></p>` for every blank line, and a document that has been
 * edited a few times collects empty headings at either end. They would show up
 * as gaps — or, worse, an empty eighth card — so nothing that renders as
 * nothing is kept.
 */
const isEmpty = (el: Element) =>
  textOf(el) === "" && !el.querySelector("img");

export const parseDestinationGuide = (
  html: string | null | undefined
): DestinationGuide | null => {
  if (!html || html.trim() === "") return null;

  const clean = DOMPurify.sanitize(html, SANITIZE_CFG);
  const body = new DOMParser().parseFromString(clean, "text/html").body;

  const nodes = Array.from(body.children).filter((el) => !isEmpty(el));

  // The heading level that carries the numbering is whatever the first
  // numbered heading uses, and only that level splits cards. Otherwise a
  // numbered sub-heading inside a card — "1. Buy a Navigo card" under
  // "Getting around" — would tear the card in half.
  const firstNumbered = nodes.find(
    (el) => isHeading(el) && NUMBERED_RE.test(textOf(el))
  );
  const cardTag = firstNumbered ? firstNumbered.tagName : null;

  // Nothing numbered to split on — an author who wrote the guide as flat prose,
  // or a city whose copy has not been reformatted yet. The panel still has to
  // open, so the whole document becomes one untitled card rather than none.
  if (!cardTag) {
    const rest = nodes.slice();
    const heading = rest.length > 0 && isHeading(rest[0]) ? rest.shift() : null;
    return {
      title: heading ? cleanTitle(textOf(heading)) : null,
      description: null,
      footer: null,
      cards:
        rest.length > 0
          ? [{ num: "01", title: "", html: rest.map((el) => el.outerHTML).join("") }]
          : [],
    };
  }

  const opensCard = (el: Element) =>
    el.tagName === cardTag && NUMBERED_RE.test(textOf(el));

  const intro: Element[] = [];
  const groups: { title: string; body: Element[] }[] = [];

  nodes.forEach((el) => {
    if (opensCard(el)) {
      groups.push({ title: cleanTitle(textOf(el)), body: [] });
    } else if (groups.length === 0) {
      intro.push(el);
    } else {
      groups[groups.length - 1].body.push(el);
    }
  });

  // A sign-off closes the document as a bare heading after the last card's
  // content ("Bon voyage et amusez-vous bien à Paris!"). The design gives it
  // its own line beside the plane rather than burying it in card seven.
  let footer: string | null = null;
  const last = groups[groups.length - 1];
  if (last && last.body.length > 0) {
    const tail = last.body[last.body.length - 1];
    if (isHeading(tail) && !NUMBERED_RE.test(textOf(tail))) {
      footer = cleanTitle(textOf(tail));
      last.body.pop();
    }
  }

  const introHeading = intro.find(isHeading);
  const introPara = intro.find((el) => !isHeading(el) && textOf(el) !== "");
  // innerHTML, not outerHTML: the caller supplies the paragraph's own styling
  // and only wants what is inside it.

  return {
    title: introHeading ? cleanTitle(textOf(introHeading)) : null,
    description: introPara ? introPara.innerHTML : null,
    footer,
    cards: groups.map((group, i) => ({
      num: String(i + 1).padStart(2, "0"),
      title: group.title,
      html: group.body.map((el) => el.outerHTML).join(""),
    })),
  };
};
